// TimeOffNotificationsComponent.js - Add this new component
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Divider,
    Paper,
    IconButton,
    Collapse
} from '@mui/material';
import {
    EventBusy as EventBusyIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Notifications as NotificationsIcon
} from '@mui/icons-material';
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from '../firebase';

function TimeOffNotificationsComponent({ orgId, userId }) {
    const [notifications, setNotifications] = useState([]);
    const [expanded, setExpanded] = useState(true);
    const [teamMembers, setTeamMembers] = useState([]);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        // 🔧 FIX: Add error boundary to prevent component from breaking dashboard
        const fetchTeamMembers = async () => {
            try {
                const usersRef = collection(db, "organizations", orgId, "users");
                const usersSnap = await getDocs(usersRef);

                const members = usersSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                setTeamMembers(members);
                return members;
            } catch (err) {
                console.error("Error fetching team members:", err);
                setHasError(true);
                return [];
            }
        };

        const fetchTimeOffNotifications = async (members) => {
            // 🔧 FIX: If we had an error before, don't retry immediately
            if (hasError) {
                console.log("Previous error detected, skipping time-off notifications fetch");
                return;
            }

            try {
                const employeeMap = new Map();
                members.forEach(member => {
                    employeeMap.set(member.id, member);
                    if (member.email) {
                        employeeMap.set(member.email.toLowerCase(), member);
                    }
                });

                // 🔧 FIX: Add timeout to prevent hanging queries
                const timeoutPromise = new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('Query timeout')), 10000)
                );

                const timeOffRef = collection(db, "organizations", orgId, "timeOffRequests");
                const q = query(
                    timeOffRef,
                    where("status", "==", "approved"),
                    orderBy("startDate", "asc")
                );

                // Race between query and timeout
                const snapshot = await Promise.race([getDocs(q), timeoutPromise]);

                const now = new Date();
                now.setHours(0, 0, 0, 0);

                const timeOffNotifications = [];

                snapshot.docs.forEach(doc => {
                    const data = doc.data();

                    if (data.userId === userId) return;

                    let startDate;
                    if (data.startDate && typeof data.startDate.toDate === 'function') {
                        startDate = data.startDate.toDate();
                    } else {
                        startDate = new Date(data.startDate);
                    }

                    const diff = Math.floor((startDate - now) / (1000 * 60 * 60 * 24));

                    if (diff >= 0 && diff <= 7) {
                        let employeeName = "Unknown";

                        if (data.userId && employeeMap.has(data.userId)) {
                            const employee = employeeMap.get(data.userId);
                            employeeName = employee.name || employee.displayName || employee.email || "Unknown";
                        } else if (data.employeeEmail && employeeMap.has(data.employeeEmail.toLowerCase())) {
                            const employee = employeeMap.get(data.employeeEmail.toLowerCase());
                            employeeName = employee.name || employee.displayName || employee.email || "Unknown";
                        }

                        let message = '';
                        if (diff === 0) {
                            message = `${employeeName} is out of office today`;
                        } else if (diff === 1) {
                            message = `${employeeName} will be out of office tomorrow`;
                        } else {
                            message = `${employeeName} will be out of office in ${diff} days`;
                        }

                        timeOffNotifications.push({
                            id: doc.id,
                            message,
                            type: data.type || 'vacation',
                            typeLabel: data.typeLabel || 'Vacation',
                            date: startDate,
                            employeeName,
                            daysUntil: diff
                        });
                    }
                });

                timeOffNotifications.sort((a, b) => a.daysUntil - b.daysUntil);
                setNotifications(timeOffNotifications);
                setHasError(false); // Reset error state on success

            } catch (err) {
                console.error("Error fetching time-off notifications:", err);

                // 🔧 FIX: Set error state but don't break the component
                setHasError(true);
                setNotifications([]); // Clear notifications on error

                // 🔧 FIX: Don't show error to user unless it's critical
                if (err.message.includes('index')) {
                    console.warn("Firestore index missing for time-off notifications. Component will be hidden until index is created.");
                }
            }
        };

        const loadData = async () => {
            const members = await fetchTeamMembers();
            if (members.length > 0) {
                await fetchTimeOffNotifications(members);
            }
        };

        loadData();

        // 🔧 FIX: Only set up refresh interval if no errors
        let refreshInterval;
        if (!hasError) {
            refreshInterval = setInterval(() => {
                if (teamMembers.length > 0 && !hasError) {
                    fetchTimeOffNotifications(teamMembers);
                } else if (!hasError) {
                    loadData();
                }
            }, 3600000); // Refresh every hour
        }

        return () => {
            if (refreshInterval) {
                clearInterval(refreshInterval);
            }
        };
    }, [orgId, userId, hasError]);

    const getTimeOffTypeColor = (type) => {
        switch (type?.toLowerCase()) {
            case 'vacation':
                return '#4CAF50'; // green
            case 'sick':
                return '#F44336'; // red
            case 'personal':
                return '#2196F3'; // blue
            case 'bereavement':
                return '#9C27B0'; // purple
            case 'jury duty':
            case 'jury':
                return '#FF9800'; // orange
            default:
                return '#607D8B'; // blue-grey
        }
    };

    // Format date for display
    const formatDate = (date) => {
        if (!date) return '';
        return new Date(date).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric'
        });
    };

    // Toggle expand/collapse
    const toggleExpanded = () => {
        setExpanded(!expanded);
    };

    // If there are no notifications, don't render anything
    if (notifications.length === 0) {
        return null;
    }

    return (
        <Paper sx={{ mb: 4, overflow: 'hidden' }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'primary.light',
                    p: 2,
                    color: 'primary.contrastText'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <NotificationsIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">
                        Team Time Off ({notifications.length})
                    </Typography>
                </Box>
                <IconButton
                    onClick={toggleExpanded}
                    sx={{ color: 'primary.contrastText' }}
                >
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>

            <Collapse in={expanded}>
                <List disablePadding>
                    {notifications.map((notification, index) => (
                        <React.Fragment key={notification.id}>
                            <ListItem alignItems="flex-start">
                                <ListItemAvatar>
                                    <Avatar sx={{ bgcolor: getTimeOffTypeColor(notification.type) }}>
                                        <EventBusyIcon />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={notification.message}
                                    secondary={
                                        <>
                                            <Typography
                                                component="span"
                                                variant="body2"
                                                color="text.primary"
                                            >
                                                {notification.typeLabel}
                                            </Typography>
                                            {' — '}
                                            <Typography
                                                component="span"
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {formatDate(notification.date)}
                                            </Typography>
                                        </>
                                    }
                                />
                            </ListItem>
                            {index < notifications.length - 1 && <Divider variant="inset" component="li" />}
                        </React.Fragment>
                    ))}
                </List>
            </Collapse>
        </Paper>
    );
}

export default TimeOffNotificationsComponent;